import React, { FunctionComponent, useState, useEffect, useCallback } from "react";
import { ProgressBar } from "react-bootstrap";
import Gantt from "../../../components/gantt";
import Toolbar from "../../../components/toolbar";
import { Claim, ProjectPlanType } from "../../../../types";
import { getGanttColor } from "../../../utils";
import Modal from "../../../components/Modal";
import "../DetailView.css";
import ErrorAlert from "../../../components/error-alert/ErrorAlert";
import RetryButton from "../../../components/error-alert/RetryButton";

type ProjectPlanProps = {
  dataSelected: Claim;
  projectPlan: ProjectPlanType[];
  requestGetProjectPlanByClaimId: (id: number) => Promise<any>;
};
const ProjectPlan: FunctionComponent<ProjectPlanProps> = (props) => {
  const { dataSelected, projectPlan, requestGetProjectPlanByClaimId } = props;
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [currentZoom, setCurrentZoom] = useState("Months");
  const [showError, setShowError] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const loadProjectPlan = useCallback(() => {
    setIsFetching(true);
    setShowError(false);
    requestGetProjectPlanByClaimId(dataSelected.id)
      .catch(() => setShowError(true))
      .finally(() => setIsFetching(false));
  }, [requestGetProjectPlanByClaimId, dataSelected]);

  useEffect(() => {
    loadProjectPlan();
  }, [loadProjectPlan]);

  const openGantt = () => {
    setShowProjectModal(true);
  };
  const getProjectPlan = () => {
    if (projectPlan.length === 0 || !projectPlan) {
      return <div>Currently no project plan.</div>;
    }

    return (
      <table className="table table-striped">
        <thead>
          <tr>
            <th className="td-style" style={{ padding: "0.35rem" }}>
              Task
            </th>
            <th className="td-style" />
            <th className="td-style" style={{ padding: "0.35rem" }}>
              Start Date
            </th>
            <th className="td-style" style={{ padding: "0.35rem" }}>
              End Date
            </th>
            <th className="td-style" style={{ padding: "0.35rem" }}>
              % Completed
            </th>
            <th className="td-style" style={{ padding: "0.35rem" }}>
              Supplier
            </th>
          </tr>
        </thead>
        <tbody>
          {projectPlan.map((projectPlan: ProjectPlanType) => {
            return (
              <tr key={projectPlan.id}>
                <td className="td-style" style={{ padding: "0.35rem" }}>
                  <div>{projectPlan.taskName}</div>
                </td>
                <td
                  className="td-style"
                  style={{
                    width: "20px",
                    padding: "0.35rem",
                    textAlign: "center",
                  }}>
                  {(projectPlan.complete === 0 && new Date(projectPlan.startDate) < new Date()) ||
                  (projectPlan.complete < 100 && new Date(projectPlan.endDate) < new Date()) ? (
                    <i
                      className="fas fa-exclamation-triangle"
                      style={{
                        color: "rgb(220,53,69)",
                        fontSize: "0.9rem",
                      }}></i>
                  ) : null}
                </td>
                <td className="td-style" style={{ padding: "0.35rem" }}>
                  {projectPlan.complete === 0 && new Date(projectPlan.startDate) < new Date() ? (
                    <div style={{ color: "rgb(220,53,69)" }}>
                      {new Date(projectPlan.startDate).toLocaleDateString("en-NZ")}
                    </div>
                  ) : (
                    <div>{new Date(projectPlan.startDate).toLocaleDateString("en-NZ")}</div>
                  )}
                </td>
                <td className="td-style" style={{ padding: "0.35rem" }}>
                  {projectPlan.complete < 100 && new Date(projectPlan.endDate) < new Date() ? (
                    <div style={{ color: "rgb(220,53,69)" }}>
                      {new Date(projectPlan.endDate).toLocaleDateString("en-NZ")}
                    </div>
                  ) : (
                    <div>{new Date(projectPlan.endDate).toLocaleDateString("en-NZ")}</div>
                  )}
                </td>
                <td className="td-style" style={{ padding: "0.35rem" }}>
                  {projectPlan.complete > 0 ? (
                    <ProgressBar now={projectPlan.complete} label={`${projectPlan.complete}%`} />
                  ) : (
                    ""
                  )}
                </td>
                <td className="td-style" style={{ padding: "0.35rem" }}>
                  <div>{projectPlan.supplier}</div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const getProjectData = () => {
    return projectPlan.map((projectPlan: ProjectPlanType) => ({
      id: parseInt(projectPlan.orderNumber),
      text: projectPlan.taskName,
      start_date: new Date(projectPlan.startDate),
      duration: projectPlan.duration,
      progress: projectPlan.complete / 100.0,
      color: getGanttColor(projectPlan.startDate, projectPlan.endDate, projectPlan.complete)
        .background,
      textColor: getGanttColor(projectPlan.startDate, projectPlan.endDate, projectPlan.complete)
        .text,
      progressColor: getGanttColor(projectPlan.startDate, projectPlan.endDate, projectPlan.complete)
        .progress,
    }));
  };

  return (
    <>
      {showProjectModal && (
        <Modal
          title="Project Plan"
          secondaryActionLabel="Close"
          secondaryAction={() => setShowProjectModal(false)}
          width="xl">
          <div>
            <div className="zoom-bar">
              <Toolbar zoom={currentZoom} onZoomChange={(zoom: string) => setCurrentZoom(zoom)} />
            </div>
            <div className="gantt-container">
              <Gantt tasks={{ data: getProjectData(), links: [] }} zoom={currentZoom} />
            </div>
          </div>
        </Modal>
      )}

      <div className="detail-wrapper">
        <div className="task-wrapper">
          <div className="heading">Project Plan</div>
          {projectPlan && projectPlan.length > 0 && (
            <div>
              <button type="button" className="btn btn-sm btn-primary" onClick={openGantt}>
                Show
              </button>
            </div>
          )}
        </div>
        {isFetching && (
          <div
            className="d-flex justify-content-center"
            style={{
              paddingTop: "1rem",
            }}>
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}
        {showError && !isFetching && (
          <div
            style={{
              display: "flex",
              flex: "0 0 auto",
              paddingTop: "1rem",
              justifyContent: "center",
            }}>
            <ErrorAlert lightMode>
              <span style={{ paddingRight: "1rem" }}>
                Error while loading the project plan of the selected claim. Please retry or submit a
                Support Desk ticket.
              </span>
              <RetryButton retryFunction={loadProjectPlan} />
            </ErrorAlert>
          </div>
        )}
        {!showError && !isFetching && getProjectPlan()}
      </div>
    </>
  );
};

export default ProjectPlan;
